// Steps/ProvisionStep.jsx
// Creates the assistant + SIP account for the wizard's receptionist via
// thirdPartyVapiProvision([{ Id: receptionistId }]), then shows the returned
// assistant_id + sip_uri.

import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Card, Label, Input, FormGroup, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import {
  thirdPartyVapiProvision,
  thirdPartyVapiProvisionSuccess,
} from "../../../../store/thirdPartyIntegration/action";

export function ProvisionStep({ wizard }) {
  const {
    dispatch,
    receptionistId,
    assistantId,
    setAssistantId,
    sipUri,
    setSipUri,
    assistantProvisioned,
    setAssistantProvisioned,
    goNext,
    goBack,
  } = wizard;

  const { provisionDataAPI, provisionLoading } = useSelector((state) => ({
    provisionDataAPI: state.ThirdPartyIntegration.thirdPartyVapiProvisionData,
    provisionLoading:
      state.ThirdPartyIntegration.thirdPartyVapiProvisionLoading,
  }));

  useEffect(() => {
    if (isEmpty(provisionDataAPI)) return;
    if (provisionDataAPI.assistant_id || provisionDataAPI.status === 200) {
      toast.success(provisionDataAPI?.message || "Assistant provisioned");
      setAssistantId(provisionDataAPI?.assistant_id || "");
      setSipUri(provisionDataAPI?.sip_uri || "");
      setAssistantProvisioned(true);
      dispatch(thirdPartyVapiProvisionSuccess([]));
    } else {
      toast.error(
        provisionDataAPI?.error ||
          provisionDataAPI?.message ||
          "Provision failed"
      );
      dispatch(thirdPartyVapiProvisionSuccess([]));
    }
  }, [provisionDataAPI]);

  const handleProvision = () => {
    if (!receptionistId) {
      toast.error("Create the receptionist first");
      return;
    }
    dispatch(thirdPartyVapiProvision([{ Id: receptionistId }]));
  };

  return (
    <div className="step-container">
      <div className="mb-4 pb-2 border-bottom">
        <h4 className="text-pastel-blue fw-bold mb-1">Create the assistant</h4>
        <p className="text-muted small mb-0">
          This creates your assistant and mints a unique SIP account to
          route calls.
        </p>
      </div>

      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <button
          type="button"
          className="btn btn-success rounded-pill px-4 fw-semibold"
          onClick={handleProvision}
          disabled={provisionLoading}
        >
          {provisionLoading ? (
            <>
              <Spinner size="sm" className="me-2" /> Creating…
            </>
          ) : (
            <>
              <i className="ri-magic-line me-2"></i>
              {assistantProvisioned ? "Recreate Assistant" : "Create Assistant"}
            </>
          )}
        </button>
        {assistantProvisioned && (
          <span className="badge bg-success">Provisioned</span>
        )}
      </div>

      {assistantProvisioned && (
        <Card className="border-0 shadow-sm rounded-4 bg-white p-4 mb-2">
          {assistantId && (
            <FormGroup>
              <Label>Assistant ID</Label>
              <Input type="text" value={assistantId} readOnly />
            </FormGroup>
          )}
          {sipUri && (
            <FormGroup className="mb-0">
              <Label>SIP URI</Label>
              <Input type="text" value={sipUri} readOnly />
              <small className="text-muted">Your unique SIP account</small>
            </FormGroup>
          )}
        </Card>
      )}

      <div className="d-flex align-items-center justify-content-between mt-4 pt-3 border-top">
        <button
          type="button"
          className="btn btn-light rounded-pill px-4 fw-semibold text-muted"
          onClick={goBack}
          disabled={provisionLoading}
        >
          <i className="ri-arrow-left-line me-2"></i> Back
        </button>
        <button
          type="button"
          className="btn btn-primary rounded-pill px-4 fw-semibold"
          onClick={goNext}
          disabled={!assistantProvisioned}
        >
          Next <i className="ri-arrow-right-line ms-2"></i>
        </button>
      </div>
    </div>
  );
}

ProvisionStep.propTypes = {
  wizard: PropTypes.object.isRequired,
};
